/**
 * Industry-standard starting points for the PO template (z8nrz7f7dz).
 *
 * Pure data: each preset turns the tenant's current layout into a complete new
 * one. The tenant's identity — logo, its placement and size, company details
 * and the default ship-to — is carried over, so switching template never loses
 * them; everything else (document settings, sections, columns, custom fields,
 * texts, accent) is the preset's own. Applying one only fills the editor's
 * draft: nothing is saved until the tenant saves, which creates a new version.
 *
 * Every preset must pass the server's checks (`layoutProblems` and the save
 * DTO in cgt-backend/src/inventory/purchase-orders/template) — presets.test.ts
 * holds them to that.
 */

import type {
  PoCustomFieldDef,
  PoTemplateColumn,
  PoTemplateField,
  PoTemplateLayout,
  PoTemplateSection,
} from '@/types/purchaseOrders'
import { canonicalJson } from './edit'

export interface PoTemplatePreset {
  id: string
  name: string
  description: string
  tags: string[]
  build: (base: PoTemplateLayout) => PoTemplateLayout
}

type Fields = [key: string, label: string, visible?: boolean][]
type Columns = [key: PoTemplateColumn['key'], label: string, visible?: boolean][]

const fields = (list: Fields): PoTemplateField[] => list.map(([key, label, visible = true]) => ({ key, label, visible }))
const columns = (list: Columns): PoTemplateColumn[] => list.map(([key, label, visible = true]) => ({ key, label, visible }))
const section = (key: PoTemplateSection['key'], title: string, opts: { visible?: boolean; fields?: Fields } = {}): PoTemplateSection => ({
  key,
  visible: opts.visible ?? true,
  title,
  ...(opts.fields ? { fields: fields(opts.fields) } : {}),
})

/** What a preset defines: the whole layout except the tenant's own identity. */
type PresetBody = Omit<PoTemplateLayout, 'branding' | 'company' | 'shipTo'> & { accentColor: string }

/**
 * The tenant's logo, company and ship-to, copied so the result shares no
 * objects with `base`. The tenant's own custom fields and attribute columns
 * are kept too, after the preset's: draft orders may already hold values for
 * those fields, and the server refuses a draft whose custom-field keys the
 * template no longer has.
 */
function withIdentity(base: PoTemplateLayout, body: PresetBody): PoTemplateLayout {
  const { accentColor, ...rest } = body
  const presetFieldKeys = new Set(rest.customFields.map((f) => f.key))
  const customFields = [
    ...rest.customFields,
    ...base.customFields
      .filter((f) => !presetFieldKeys.has(f.key))
      .map((f) => ({ ...f, ...(f.options ? { options: [...f.options] } : {}) })),
  ].slice(0, 30)
  const presetColumnKeys = new Set(rest.columns.map((c) => c.key))
  const columns = [
    ...rest.columns,
    // Kept but hidden, so the template looks as designed; one switch shows them again.
    ...base.columns
      .filter((c) => c.key.startsWith('attr:') && !presetColumnKeys.has(c.key))
      .map((c) => ({ ...c, visible: false })),
  ]
  return {
    branding: {
      logoAssetKey: base.branding.logoAssetKey,
      logoPosition: base.branding.logoPosition,
      logoHeightMm: base.branding.logoHeightMm,
      accentColor,
    },
    company: { ...base.company, address: base.company.address ? { ...base.company.address } : base.company.address },
    shipTo: { ...base.shipTo },
    ...rest,
    customFields,
    columns,
  }
}

// ─── Standard commercial (US) ───────────────────────────────────────────────

const US_TERMS = `### 1. Acceptance
This Purchase Order is accepted by the Seller's written acknowledgement or by shipment of any goods ordered. Acceptance is limited to these terms; additional or different terms proposed by the Seller are rejected unless {{company.legalName}} agrees to them in writing.

### 2. Shipment & Delivery
Time is of the essence. Ship on the delivery date and by the method stated; the PO number must appear on all packing slips, bills of lading and invoices. Risk of loss passes to Buyer on delivery to the ship-to address (F.O.B. Destination) unless stated otherwise.

### 3. Inspection
All goods are subject to Buyer's inspection and approval within a reasonable time after delivery. Non-conforming goods may be rejected and returned at Seller's risk and expense.

### 4. Price & Payment
Prices are firm as stated. Invoices are payable on the terms stated on this order, counted from receipt of a correct invoice or of the goods, whichever is later.

### 5. Warranty
Seller warrants that the goods are merchantable, fit for their intended purpose, free from defects, and conform to all specifications, samples and descriptions.

### 6. Compliance & Indemnity
Seller shall comply with all applicable federal, state and local laws, and shall indemnify Buyer against claims arising from the goods, including infringement of intellectual property.

### 7. Governing Law
This order is governed by the Uniform Commercial Code as adopted in the state of Buyer's principal place of business.`

const usCommercial: PoTemplatePreset = {
  id: 'us-commercial',
  name: 'Standard commercial (US)',
  description: 'Classic US purchase order: Letter paper, USD, Net 30, F.O.B. and a signature block.',
  tags: ['US', 'Commercial', 'Letter'],
  build: (base) =>
    withIdentity(base, {
      accentColor: '#1d4ed8',
      document: {
        title: 'PURCHASE ORDER',
        paperSize: 'Letter',
        locale: 'en-US',
        currency: 'USD',
        numbering: { prefix: 'PO', datePart: 'YYYY', separator: '-', padding: 5 },
        defaults: { taxRatePct: 0, paymentTerms: 'Net 30', incoterms: 'FOB Destination' },
      },
      sections: [
        section('header', 'PURCHASE ORDER', {
          fields: [
            ['number', 'P.O. Number'],
            ['date', 'Date'],
            ['revision', 'Revision'],
            ['requestedDeliveryDate', 'Delivery Date'],
          ],
        }),
        section('supplier', 'Vendor'),
        section('shipTo', 'Ship To'),
        section('billTo', 'Bill To'),
        section('orderTerms', 'Order Details', {
          fields: [
            ['buyer', 'Requisitioner'],
            ['shippingMethod', 'Ship Via'],
            ['incoterms', 'F.O.B.'],
            ['paymentTerms', 'Terms'],
            ['reference', 'Reference'],
            ['currency', 'Currency', false],
          ],
        }),
        section('lines', 'Items'),
        section('totals', 'Totals', {
          fields: [
            ['subtotal', 'Subtotal'],
            ['discountTotal', 'Discount'],
            ['shipping', 'Shipping & Handling'],
            ['tax', 'Sales Tax'],
            ['grandTotal', 'Total'],
          ],
        }),
        section('notes', 'Comments or Special Instructions'),
        section('authorization', 'Authorization', {
          fields: [
            ['approvedBy', 'Authorized by'],
            ['title', 'Title'],
            ['date', 'Date'],
            ['signature', 'Signature'],
          ],
        }),
        section('terms', 'Terms and Conditions'),
        section('footer', 'Footer'),
      ],
      columns: columns([
        ['lineNo', '#'],
        ['articleNo', 'Item #'],
        ['supplierRef', 'Vendor Part #', false],
        ['description', 'Description'],
        ['qty', 'Qty'],
        ['uom', 'Unit'],
        ['unitPrice', 'Unit Price'],
        ['discountPct', 'Disc %', false],
        ['taxPct', 'Tax %', false],
        ['lineTotal', 'Amount'],
      ]),
      customFields: [],
      textBlocks: {
        terms: US_TERMS,
        defaultNotes: '',
        footer:
          'Please reference the P.O. number on all invoices, packing slips and correspondence.\nQuestions about this order: {{company.email}} · {{company.phone}}',
      },
    }),
}

// ─── European (EU VAT) ──────────────────────────────────────────────────────

const EU_TERMS = `### 1. Scope
These general purchase conditions apply to every order of {{company.legalName}}. The supplier's own terms of sale do not apply, even if referred to in an order confirmation, unless accepted by us in writing.

### 2. Order & confirmation
The supplier confirms the order in writing within 3 working days, stating the delivery date. Changes are valid only as a new revision of this order.

### 3. Delivery & transfer of risk
Delivery is made according to the Incoterms® 2020 rule stated on the order (DAP unless agreed otherwise), with a delivery note quoting our order number. Partial deliveries require our prior consent.

### 4. Prices & invoicing
Prices are net, exclusive of VAT, and fixed. Invoices must comply with Directive 2006/112/EC and state our order number, both parties' VAT identification numbers, and the applicable VAT rate or the legal basis for exemption (e.g. intra-Community supply, reverse charge).

### 5. Payment
Payment is made on the terms stated on the order, from receipt of a correct invoice and conforming goods. The statutory provisions on late payment (Directive 2011/7/EU) apply.

### 6. Warranty & liability
The supplier warrants that the goods conform to the order, the agreed specification and the applicable EU legislation (including REACH and RoHS where relevant). The statutory warranty rights apply in full.

### 7. Data protection
Personal data exchanged under this order is processed in accordance with Regulation (EU) 2016/679 (GDPR) and only for the purpose of performing it.

### 8. Applicable law & jurisdiction
This order is governed by the law of the country in which {{company.legalName}} is registered, excluding the UN Convention on Contracts for the International Sale of Goods (CISG). The courts of our registered office have exclusive jurisdiction.`

const euVat: PoTemplatePreset = {
  id: 'eu-vat',
  name: 'European (EU VAT)',
  description: 'A4, EUR with 21% VAT, Incoterms® DAP, VAT IDs on both parties and EU purchase conditions.',
  tags: ['EU', 'VAT', 'A4'],
  build: (base) =>
    withIdentity(base, {
      accentColor: '#0f766e',
      document: {
        title: 'Purchase Order',
        paperSize: 'A4',
        locale: 'en-GB',
        currency: 'EUR',
        numbering: { prefix: 'PO', datePart: 'YYYY', separator: '/', padding: 5 },
        defaults: { taxRatePct: 21, paymentTerms: '30 days from invoice date', incoterms: 'DAP (Incoterms® 2020)' },
      },
      sections: [
        section('header', 'Purchase Order', {
          fields: [
            ['number', 'Order no.'],
            ['date', 'Order date'],
            ['revision', 'Revision'],
            ['requestedDeliveryDate', 'Delivery date'],
          ],
        }),
        section('supplier', 'Supplier', {
          fields: [
            ['name', 'Name'],
            ['contact', 'Contact'],
            ['address', 'Address'],
            ['phone', 'Phone'],
            ['email', 'Email'],
            ['taxId', 'VAT number'],
          ],
        }),
        section('billTo', 'Invoice address', {
          fields: [
            ['name', 'Name'],
            ['street', 'Street'],
            ['city', 'City'],
            ['country', 'Country'],
            ['email', 'Email'],
            ['taxId', 'VAT number'],
          ],
        }),
        section('shipTo', 'Delivery address'),
        section('orderTerms', 'Order terms', {
          fields: [
            ['buyer', 'Contact'],
            ['reference', 'Your reference'],
            ['shippingMethod', 'Delivery method'],
            ['incoterms', 'Incoterms®'],
            ['paymentTerms', 'Payment terms'],
            ['currency', 'Currency'],
          ],
        }),
        section('lines', 'Order lines'),
        section('notes', 'Remarks'),
        section('totals', 'Totals', {
          fields: [
            ['subtotal', 'Net total'],
            ['discountTotal', 'Discount'],
            ['shipping', 'Freight'],
            ['tax', 'VAT'],
            ['grandTotal', 'Total incl. VAT'],
          ],
        }),
        section('terms', 'General purchase conditions'),
        section('authorization', 'Authorisation', {
          fields: [
            ['approvedBy', 'Authorised by'],
            ['date', 'Date'],
            ['signature', 'Signature'],
          ],
        }),
        section('footer', 'Footer'),
      ],
      columns: columns([
        ['lineNo', 'Pos.'],
        ['articleNo', 'Article no.'],
        ['supplierRef', 'Supplier ref.'],
        ['description', 'Description'],
        ['qty', 'Qty'],
        ['uom', 'Unit'],
        ['unitPrice', 'Unit price'],
        ['discountPct', 'Disc. %'],
        ['taxPct', 'VAT %'],
        ['lineTotal', 'Net amount'],
      ]),
      customFields: [],
      textBlocks: {
        terms: EU_TERMS,
        defaultNotes: '',
        footer:
          '{{company.legalName}} · {{company.address}} · VAT {{company.taxId}} · Reg. {{company.registration}}\nInvoices must quote our order number and the VAT identification numbers of both parties.',
      },
    }),
}

// ─── Life sciences / GMP ────────────────────────────────────────────────────

const GMP_TERMS = `### Quality requirements
### 1. GMP / GDP compliance
Goods are manufactured, tested, stored and distributed in compliance with current Good Manufacturing Practice (EU GMP Part I/II, 21 CFR 210/211 as applicable) and Good Distribution Practice (EU GDP 2013/C 343/01). The supplier must be on the approved supplier list of {{company.legalName}}; the quality agreement in force between the parties prevails over these terms.

### 2. Certificate of Analysis
Every lot is delivered with a Certificate of Analysis (CoA) signed by the supplier's quality unit, stating the lot number, manufacturing and expiry dates and the results against the agreed specification. Deliveries without a CoA are held in quarantine and may be rejected.

### 3. Temperature-controlled delivery
Material with a storage condition is shipped in qualified packaging within that condition, with continuous temperature monitoring. The data-logger record accompanies the delivery; any excursion is reported before shipment is accepted.

### 4. Shelf life & traceability
Remaining shelf life on receipt is at least 12 months, or 75% of total shelf life where that is shorter. Each container is labelled with material code, lot number and expiry date; a delivery contains as few lots as possible, each listed on the delivery note.

### 5. Change notification
The supplier notifies {{company.legalName}} in writing at least 90 days before any change to the manufacturing site, process, specification, raw-material source, packaging or test method affecting the goods.

### 6. Audits & non-conformance
{{company.legalName}} and regulatory authorities may audit the supplier on reasonable notice. Non-conforming goods are rejected at the supplier's expense; deviations and complaints are investigated with a root-cause analysis and CAPA.

## Commercial terms
Delivery to the ship-to address on the stated Incoterms, quoting the PO number on all documents. Invoices are paid on the stated payment terms from receipt of a correct invoice and released goods. Governed by the law of the jurisdiction in which {{company.legalName}} is registered.`

const GMP_CUSTOM_FIELDS: PoCustomFieldDef[] = [
  { key: 'coaRequired', label: 'CoA required', type: 'select', options: ['Yes', 'No'], required: true },
  {
    key: 'storageCondition',
    label: 'Storage condition',
    type: 'select',
    options: [
      'Ambient (15–25 °C)',
      'Refrigerated (2–8 °C)',
      'Frozen (≤ −20 °C)',
      'Ultra-low (≤ −70 °C)',
      'Cryogenic (LN₂ vapour, ≤ −150 °C)',
    ],
    required: true,
  },
  { key: 'qualityAgreementRef', label: 'Quality agreement ref.', type: 'text' },
]

const lifeSciencesGmp: PoTemplatePreset = {
  id: 'life-sciences-gmp',
  name: 'Life sciences / GMP',
  description: 'For GMP materials: CoA per lot, cold-chain delivery, change notification, shelf life and QA approval.',
  tags: ['Life sciences', 'GMP', 'GDP', 'A4'],
  build: (base) =>
    withIdentity(base, {
      accentColor: '#047857',
      document: {
        title: 'GMP Purchase Order',
        paperSize: 'A4',
        locale: 'en-GB',
        currency: 'EUR',
        numbering: { prefix: 'QPO', datePart: 'YY', separator: '-', padding: 5 },
        defaults: { taxRatePct: 21, paymentTerms: '45 days from receipt of released goods', incoterms: 'DAP' },
      },
      sections: [
        section('header', 'GMP Purchase Order', {
          fields: [
            ['number', 'PO Number'],
            ['date', 'Date'],
            ['revision', 'Revision'],
            ['requestedDeliveryDate', 'Required on site'],
          ],
        }),
        section('supplier', 'Approved supplier'),
        section('shipTo', 'Deliver to (goods receipt)'),
        section('billTo', 'Invoice to'),
        section('orderTerms', 'Order & quality details', {
          fields: [
            ['buyer', 'Buyer'],
            ['shippingMethod', 'Ship via'],
            ['incoterms', 'Incoterms'],
            ['paymentTerms', 'Payment terms'],
            ['reference', 'Reference'],
            ['currency', 'Currency', false],
          ],
        }),
        section('lines', 'Materials'),
        section('notes', 'Quality notes'),
        section('totals', 'Totals', {
          fields: [
            ['subtotal', 'Subtotal'],
            ['discountTotal', 'Discount'],
            ['shipping', 'Freight & cold chain'],
            ['tax', 'VAT'],
            ['grandTotal', 'Total'],
          ],
        }),
        section('authorization', 'Approval', {
          fields: [
            ['requestedBy', 'Requested by'],
            ['approvedBy', 'Approved by (Purchasing)'],
            ['qaApproval', 'QA approval'],
            ['date', 'Date'],
          ],
        }),
        section('terms', 'Quality & purchase terms'),
        section('footer', 'Footer'),
      ],
      columns: columns([
        ['lineNo', 'Line'],
        ['articleNo', 'Material code'],
        ['supplierRef', 'Supplier part no.'],
        ['description', 'Description / grade'],
        ['qty', 'Qty'],
        ['uom', 'UoM'],
        ['unitPrice', 'Unit price'],
        ['discountPct', 'Disc. %', false],
        ['taxPct', 'VAT %', false],
        ['lineTotal', 'Amount'],
      ]),
      customFields: GMP_CUSTOM_FIELDS.map((f) => ({ ...f, ...(f.options ? { options: [...f.options] } : {}) })),
      textBlocks: {
        terms: GMP_TERMS,
        defaultNotes:
          'Supply each lot with a signed Certificate of Analysis. Ship temperature-controlled within the stated storage condition and include the data-logger record.',
        footer:
          '{{company.legalName}} · VAT {{company.taxId}}\nGMP material: deliveries are quarantined until QA release. Quote the PO number on every delivery note, CoA and invoice.',
      },
    }),
}

// ─── Cryoport classic (Bon de commande) ─────────────────────────────────────

const CGA_TERMS = `### Conditions générales d'achat
### 1. Champ d'application
Les présentes conditions générales d'achat s'appliquent à toute commande passée par {{company.legalName}}. Elles prévalent sur les conditions générales de vente du fournisseur, sauf accord écrit contraire de {{company.legalName}}.

### 2. Acceptation de la commande
Toute commande doit être confirmée par écrit dans les 3 jours ouvrables. À défaut, le début d'exécution vaut acceptation sans réserve des présentes conditions.

### 3. Livraison
Les marchandises sont livrées à l'adresse et à la date indiquées sur le bon de commande, accompagnées d'un bon de livraison mentionnant le numéro de commande. Tout retard prévisible doit être signalé immédiatement à {{company.legalName}}.

### 4. Réception et conformité
{{company.legalName}} se réserve le droit de refuser toute marchandise non conforme, endommagée ou livrée hors des conditions de stockage prévues, aux frais et risques du fournisseur.

### 5. Prix et facturation
Les prix sont fermes et s'entendent hors TVA. Les factures mentionnent le numéro de commande et le numéro de TVA des deux parties ; elles sont payables selon les conditions de paiement indiquées.

### 6. Garantie
Le fournisseur garantit que les marchandises sont conformes à leur spécification et à leur certificat d'analyse, et exemptes de tout vice.

### 7. Droit applicable
Les présentes conditions sont régies par le droit belge. Tout litige relève de la compétence exclusive des tribunaux du siège de {{company.legalName}}.`

const cryoportClassic: PoTemplatePreset = {
  id: 'cryoport-classic',
  name: 'Cryoport classic (Bon de commande)',
  description: 'The French-language Belgian layout: ARN numbering, TVA 21% and conditions générales d’achat.',
  tags: ['EU', 'Belgium', 'Français'],
  build: (base) =>
    withIdentity(base, {
      accentColor: '#4338ca',
      document: {
        title: 'Bon de commande',
        paperSize: 'A4',
        locale: 'fr-BE',
        currency: 'EUR',
        numbering: { prefix: 'ARN', datePart: 'YYMM', separator: '-', padding: 0 },
        defaults: { taxRatePct: 21, paymentTerms: '30 jours fin de mois', incoterms: 'DAP' },
      },
      sections: [
        section('header', 'Bon de commande', {
          fields: [
            ['number', 'N° de commande'],
            ['date', 'Date'],
            ['revision', 'Révision'],
            ['requestedDeliveryDate', 'Livraison souhaitée'],
          ],
        }),
        section('supplier', 'Fournisseur'),
        section('shipTo', 'Adresse de livraison'),
        section('billTo', 'Adresse de facturation'),
        section('orderTerms', 'Détails de la commande', {
          fields: [
            ['buyer', 'Acheteur'],
            ['shippingMethod', "Mode d'expédition"],
            ['incoterms', 'Incoterms'],
            ['paymentTerms', 'Conditions de paiement'],
            ['reference', 'Référence'],
            ['currency', 'Devise'],
          ],
        }),
        section('lines', 'Lignes de commande'),
        section('totals', 'Totaux', {
          fields: [
            ['subtotal', 'Total'],
            ['discountTotal', 'Remise'],
            ['shipping', 'Frais de port'],
            ['tax', 'TVA'],
            ['grandTotal', 'Total TTC'],
          ],
        }),
        section('notes', 'Remarques'),
        section('authorization', 'Approbation', {
          fields: [
            ['approvedBy', 'Approuvé par'],
            ['name', 'Nom'],
            ['date', 'Date'],
            ['signature', 'Signature'],
          ],
        }),
        section('terms', "Conditions générales d'achat"),
        section('footer', 'Pied de page'),
      ],
      columns: columns([
        ['lineNo', 'Line'],
        ['articleNo', 'Nr Article'],
        ['supplierRef', 'Supplier Ref'],
        ['description', 'Nom'],
        ['qty', 'Quantity'],
        ['uom', 'Unité', false],
        ['unitPrice', 'Unit Price'],
        ['discountPct', 'Remise %', false],
        ['taxPct', 'TVA %', false],
        ['lineTotal', 'Total'],
      ]),
      customFields: [],
      textBlocks: {
        terms: CGA_TERMS,
        defaultNotes: '',
        footer:
          '{{company.legalName}} · TVA {{company.taxId}} · {{company.registration}}\nMerci de mentionner le numéro de commande sur toutes vos factures, bons de livraison et correspondances.',
      },
    }),
}

// ─── Minimal / services ─────────────────────────────────────────────────────

const SERVICES_TERMS = `- Services are performed as described on this order, with reasonable skill and care, by the date stated.
- Invoices quote the PO number and are paid on the stated payment terms from receipt of a correct invoice.
- Work beyond the amounts on this order needs a revised order from {{company.legalName}} before it starts.
- Confidential information received while performing the services is used only for this order.`

const minimalServices: PoTemplatePreset = {
  id: 'minimal-services',
  name: 'Minimal / services',
  description: 'A compact one-pager for services and simple purchases: description-led lines, no shipping or Incoterms.',
  tags: ['Services', 'Compact', 'Any region'],
  // Region-neutral: keeps the tenant's paper, locale, currency and tax rate.
  build: (base) =>
    withIdentity(base, {
      accentColor: '#334155',
      document: {
        title: 'Purchase Order',
        paperSize: base.document.paperSize,
        locale: base.document.locale,
        currency: base.document.currency,
        numbering: { prefix: 'SPO', datePart: 'YY', separator: '-', padding: 3 },
        defaults: { taxRatePct: base.document.defaults.taxRatePct, paymentTerms: '14 days', incoterms: null },
      },
      sections: [
        section('header', 'Purchase Order', {
          fields: [
            ['number', 'PO Number'],
            ['date', 'Date'],
            ['revision', 'Revision'],
            ['requestedDeliveryDate', 'Due by'],
          ],
        }),
        section('supplier', 'Supplier'),
        section('billTo', 'Bill to'),
        section('shipTo', 'Ship to', { visible: false }),
        section('orderTerms', 'Details', {
          fields: [
            ['buyer', 'Requested by'],
            ['shippingMethod', 'Ship via', false],
            ['incoterms', 'Incoterms', false],
            ['paymentTerms', 'Payment terms'],
            ['reference', 'Reference'],
            ['currency', 'Currency', false],
          ],
        }),
        section('lines', 'Services'),
        section('totals', 'Totals', {
          fields: [
            ['subtotal', 'Subtotal'],
            ['discountTotal', 'Discount', false],
            ['shipping', 'Shipping', false],
            ['tax', 'Tax'],
            ['grandTotal', 'Total'],
          ],
        }),
        section('notes', 'Scope'),
        section('authorization', 'Authorization', { visible: false }),
        section('terms', 'Terms'),
        section('footer', 'Footer'),
      ],
      columns: columns([
        ['lineNo', '#', false],
        ['articleNo', 'Item', false],
        ['supplierRef', 'Supplier ref', false],
        ['description', 'Description'],
        ['qty', 'Qty'],
        ['uom', 'Unit', false],
        ['unitPrice', 'Rate'],
        ['discountPct', 'Disc %', false],
        ['taxPct', 'Tax %', false],
        ['lineTotal', 'Amount'],
      ]),
      customFields: [],
      textBlocks: {
        terms: SERVICES_TERMS,
        defaultNotes: '',
        footer: '{{company.legalName}} · {{company.email}}',
      },
    }),
}

/** The gallery, in display order. */
export const PO_TEMPLATE_PRESETS: PoTemplatePreset[] = [
  usCommercial,
  euVat,
  lifeSciencesGmp,
  cryoportClassic,
  minimalServices,
]


/** Whether applying `preset` to `layout` would change nothing — i.e. it is the template in use. */
export function presetMatches(preset: PoTemplatePreset, layout: PoTemplateLayout): boolean {
  return canonicalJson(preset.build(layout)) === canonicalJson(layout)
}
